/**
 * NegotiationDetailView - Right panel view for negotiation finding details
 *
 * Task 303: NegotiationDetailView + right panel wiring
 *
 * Pushed when user clicks a negotiation finding annotation.
 * Shows finding details, response strength selector, comment audience toggle,
 * suggested actions, scoped chat, and "Draft response" button.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { NegotiationContext, ClauseContext } from '../../../stores/deskStore';
import { useDeskStore } from '../../../stores/deskStore';
import { useDocumentStore } from '../../../stores/documentStore';
import { apiService } from '../../../services/api';
import { negotiationApi, type NegotiationDraftResult } from '../../../services/api/negotiationApi';
import { useDraftNegotiationResponse, useUpdateNegotiationFinding, useUpdateClauseState } from '../../../queries/mutations/useNegotiationMutations';
import { useClauseNegotiationStates } from '../../../hooks/useClauseNegotiationStates';
import { computeWordDiff } from '../utils/wordDiff';
import { normalizeSeverity } from '../utils/normalizeSeverity';
// Plan 180: dismiss/restore via the same mutation hooks the cards use.
import { useDismissNegotiationFinding, useRestoreNegotiationFinding } from '../../../queries/mutations/useDismissNegotiationFinding';
import { useNotifications } from '../../NotificationSystem';
// Plan 196: right-pane attribution block when the agent grounded the
// finding in a firm playbook position.
import { PlaybookAttribution } from './PlaybookAttribution';
import { useScopedChatSession } from '../../../queries/useScopedChatSession';

// Issue #784: per-submit idempotency-key generator.
// crypto.randomUUID exists in modern browsers + jsdom; fallback otherwise.
function mintIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
import '../RightPanelContainer.css';

interface NegotiationDetailViewProps {
  context: NegotiationContext;
  clauseContext?: ClauseContext;
  onBack: () => void;
  onRedraft?: (ctx: any) => void;
}

export type ResponseStrength =
  | 'completely_reject'
  | 'partially_reject'
  | 'balanced'
  | 'partially_accept'
  | 'completely_accept';

export type CommentAudience = 'internal' | 'external';

const RESPONSE_STRENGTHS: { value: ResponseStrength; label: string; description: string }[] = [
  { value: 'completely_reject', label: 'Reject', description: 'Propose text close to your original position' },
  { value: 'partially_reject', label: 'Push back', description: 'Counter-propose, closer to your original' },
  { value: 'balanced', label: 'Balanced', description: 'Find middle ground between positions' },
  { value: 'partially_accept', label: 'Accept with protections', description: 'Accept with protective conditions added' },
  { value: 'completely_accept', label: 'Accept', description: 'Accept counterparty text as-is' },
];

const VALID_RESPONSE_STRENGTHS = new Set<string>(RESPONSE_STRENGTHS.map((s) => s.value));

const SEVERITY_LABELS: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const FINDING_TYPE_LABELS: Record<string, string> = {
  negotiated_position: 'Negotiated Position',
  untouched_opportunity: 'Untouched Opportunity',
};

/** Inline word-level diff display for clause revisions */
function RevisionDiff({ original, revised }: { original: string; revised: string }) {
  const segments = useMemo(() => computeWordDiff(original, revised), [original, revised]);
  return (
    <div style={{ lineHeight: 1.6, fontSize: '12.5px' }}>
      {segments.map((seg, i) => {
        if (seg.type === 'equal') return <span key={i}>{seg.text}</span>;
        if (seg.type === 'removed') return <del key={i} style={{ background: '#fecaca', color: '#991b1b', textDecoration: 'line-through' }}>{seg.text}</del>;
        return <ins key={i} style={{ background: '#bbf7d0', color: '#166534', textDecoration: 'none' }}>{seg.text}</ins>;
      })}
    </div>
  );
}

export const NegotiationDetailView: React.FC<NegotiationDetailViewProps> = ({
  context,
  clauseContext,
  onBack,
  onRedraft,
}) => {
  const [responseStrength, setResponseStrength] = useState<ResponseStrength | null>(
    context.recommendationStrength as ResponseStrength || null
  );
  const [commentAudience, setCommentAudience] = useState<CommentAudience>('external');

  // Draft state
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftResult, setDraftResult] = useState<NegotiationDraftResult | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [initialDraftLoading, setInitialDraftLoading] = useState(true);

  // Live clause state from TanStack Query (auto-invalidated by mutations)
  const { statesByClause } = useClauseNegotiationStates(context.analysisRunId || null);
  const liveClauseState = context.clauseId ? statesByClause.get(context.clauseId) : undefined;
  const isAccepted = liveClauseState?.status === 'accepted';

  // Plan 180: dismiss/restore state + mutations.
  const [dismissing, setDismissing] = useState(false);
  const dismissNegMut = useDismissNegotiationFinding(context.analysisRunId || '');
  const restoreNegMut = useRestoreNegotiationFinding(context.analysisRunId || '');
  const { showSuccess: showSuccessNeg, showError: showErrorNeg } = useNotifications();

  const handleDismiss = async () => {
    if (dismissing || !context.analysisRunId) return;
    setDismissing(true);
    try {
      await dismissNegMut.mutateAsync({ findingId: context.findingId });
      // Plan 41: helper drops the negotiationDetail frame + any buried
      // frames. Replaces the prior setTimeout(onBack, 800) lag.
      const beforeStackLength = useDeskStore.getState().rightPanelViewStack.length;
      useDeskStore.getState().clearRightPaneForDismissedFinding('negotiation', context.findingId);
      const afterStackLength = useDeskStore.getState().rightPanelViewStack.length;
      showSuccessNeg('Finding dismissed', {
        action: {
          label: 'Undo',
          onClick: () => restoreNegMut.mutate({ findingId: context.findingId }),
        },
        duration: 5000,
      });
      // Defensive: if the helper didn't change the stack (impossible here),
      // fall back to onBack() so we never strand the user on a stale view.
      if (afterStackLength === beforeStackLength) {
        console.warn('[NegotiationDetailView] clearRightPaneForDismissedFinding did not change stack; falling back to onBack()');
        onBack();
      }
    } catch (err) {
      console.error('[NegotiationDetailView] Dismiss error:', err);
      showErrorNeg('Failed to dismiss finding', 'Try again');
      setDismissing(false);
    }
  };

  // Refine state
  const [showRefineInput, setShowRefineInput] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');

  // Scoped chat state
  const [chatInput, setChatInput] = useState('');
  // Plan 235 / #967 (PR C): an ORDERED thread, not a single latest response.
  // Prior turns must survive navigation like every other scoped chat.
  const [chatMessages, setChatMessages] = useState<
    { id: string; role: 'user' | 'assistant'; content: string }[]
  >([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [hydrationError, setHydrationError] = useState<string | null>(null);
  const [hydrationAttempt, setHydrationAttempt] = useState(0);
  const [hydratedSession, setHydratedSession] = useState<string | null>(null);
  // Synchronous guard. `chatLoading` is React state, so two submits in the
  // same frame both observe false and both dispatch.
  const sendInFlightRef = useRef(false);

  // Plan 235 / #967: a persisted per-finding session. `finding` scope already
  // covers negotiation_findings server-side (prompt_improvement_service
  // _FINDING_TABLE_DOC_COLUMNS), so no schema or API change is required.
  const selectedMatterId = useDeskStore((state) => state.selectedMatterId);
  const { sessionId, isLoading: sessionLoading } = useScopedChatSession(
    selectedMatterId,
    'finding',
    context.findingId,
    `Redline: ${context.findingTitle || 'Finding'}`,
  );
  const activeDocumentId = useDocumentStore((state) => state.activeDocumentId);
  const bodyRef = useRef<HTMLDivElement>(null);

  // TanStack Query mutations
  const draftResponseMutation = useDraftNegotiationResponse();
  const updateFindingMutation = useUpdateNegotiationFinding();
  const updateClauseStateMutation = useUpdateClauseState();

  const scrollToBottom = () => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (chatMessages.length > 0) scrollToBottom();
  }, [chatMessages.length]);

  // Load existing draft on mount (always attempt — silently no-ops if no revision exists)
  useEffect(() => {
    if (!context.findingId || !context.analysisRunId) return;
    if (draftResult) return;

    let cancelled = false;
    (async () => {
      try {
        const revResp = await negotiationApi.getFindingRevisions(context.analysisRunId, context.findingId);
        if (cancelled || !revResp.data) return;
        const rev = revResp.data;
        // Only set draft result if there's actually a revision or comment
        if (!rev.baseline_text && !rev.proposed_text && !rev.comment_text) return;
        setDraftResult({
          clause_id: context.clauseId || '',
          cost: 0,
          turns: 0,
          tool_calls_count: 0,
          has_revision: !!rev.baseline_text && !!rev.proposed_text,
          has_comment: !!rev.comment_text,
          comment_text: rev.comment_text || null,
          comment_audience: rev.comment_audience || null,
          baseline_text: rev.baseline_text || null,
          proposed_text: rev.proposed_text || null,
          proximity_warning: false,
          stop_reason: null,
          result: null,
        });
        // Issue #864: restore the saved comment audience so the summary row and
        // the prompt toggle reflect the loaded draft instead of the default.
        if (rev.comment_audience === 'internal' || rev.comment_audience === 'external') {
          setCommentAudience(rev.comment_audience);
        }
      } catch (err) {
        // Silently fail — user can still re-draft
        console.warn('[NegotiationDetailView] Failed to load existing draft:', err);
      } finally {
        if (!cancelled) setInitialDraftLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [context.findingId, context.analysisRunId]);

  // Issue #864: restore the user's previously-selected strength (persisted to
  // clause_negotiation_state.selected_strength during drafting, negotiation.py:785).
  // context.recommendationStrength is only the seed; the user may have overridden it.
  // Two guards: strengthHydrated stops a refetch re-applying the stored value after
  // the first hydration; strengthTouchedRef stops a LATE first arrival clobbering an
  // in-session change made (via the radio) before clause-state loaded. Idempotent
  // value-set => StrictMode-safe.
  const strengthTouchedRef = useRef(false);
  const strengthHydrated = useRef(false);
  useEffect(() => {
    if (strengthHydrated.current || strengthTouchedRef.current) return;
    const stored = liveClauseState?.selected_strength;
    if (stored && VALID_RESPONSE_STRENGTHS.has(stored)) {
      setResponseStrength(stored as ResponseStrength);
      strengthHydrated.current = true;
    }
  }, [liveClauseState]);

  // Plan 58: defence-in-depth — fold pre-migration critical rows to high.
  const severityClass = normalizeSeverity(context.severity);
  const findingTypeLabel = FINDING_TYPE_LABELS[context.findingType] || context.findingType;

  const handleAcceptChanges = async () => {
    // "Completely accept" shortcut: skip drafting agent, mark as accepted (P=C)
    setDraftLoading(true);
    setDraftError(null);
    try {
      await updateFindingMutation.mutateAsync({
        analysisRunId: context.analysisRunId,
        findingId: context.findingId,
        data: { status: 'accepted', recommendation_strength: 'completely_accept' },
      });
      // Also update clause state to accepted (accept as-is, no revision)
      if (context.clauseId) {
        await updateClauseStateMutation.mutateAsync({
          analysisRunId: context.analysisRunId,
          clauseId: context.clauseId,
          data: { status: 'accepted' },
        });
      }
      onBack();
    } catch (err) {
      console.error('[NegotiationDetailView] Accept error:', err);
      setDraftError(err instanceof Error ? err.message : 'Failed to accept changes.');
    } finally {
      setDraftLoading(false);
    }
  };

  const handleDraftResponse = async (refinement?: string) => {
    if (!responseStrength || draftLoading) return;

    // Shortcut: completely accept skips drafting agent
    if (responseStrength === 'completely_accept') {
      await handleAcceptChanges();
      return;
    }

    setDraftLoading(true);
    setDraftError(null);
    setDraftResult(null);
    setShowRefineInput(false);

    try {
      const data: { response_strength: string; comment_audience: string; refinement_prompt?: string } = {
        response_strength: responseStrength,
        comment_audience: commentAudience,
      };
      if (refinement) data.refinement_prompt = refinement;

      const response = await draftResponseMutation.mutateAsync({
        analysisRunId: context.analysisRunId,
        findingId: context.findingId,
        data,
      });
      setDraftResult(response.data.draft_result);
    } catch (err) {
      console.error('[NegotiationDetailView] Draft error:', err);
      setDraftError(err instanceof Error ? err.message : 'Failed to draft response.');
    } finally {
      setDraftLoading(false);
    }
  };

  const handleAcceptDraft = async () => {
    if (!draftResult) return;
    try {
      await updateFindingMutation.mutateAsync({ analysisRunId: context.analysisRunId, findingId: context.findingId, data: { status: 'accepted' } });
      // Update clause state to accepted
      if (context.clauseId) {
        await updateClauseStateMutation.mutateAsync({
          analysisRunId: context.analysisRunId,
          clauseId: context.clauseId,
          data: { status: 'accepted' },
        });
      }
    } catch (err) {
      console.error('[NegotiationDetailView] Failed to update finding/clause status:', err);
    }
  };

  const handleRefineDraft = () => {
    setShowRefineInput(true);
    setRefinePrompt('');
  };

  const handleSubmitRefinement = () => {
    if (!refinePrompt.trim()) return;
    handleDraftResponse(refinePrompt.trim());
    setRefinePrompt('');
  };

  // Plan 235 / #967: hydrate the persisted thread. Deliberately NO
  // ref-set-before-fetch guard -- that is StrictMode-hostile and sticks the
  // loader on warm re-focus (Issue #844). The `cancelled` flag discards stale
  // results; the `current.length > 0` check avoids clobbering an in-flight
  // conversation, which is the race guard against late hydration overwriting
  // a message submitted meanwhile.
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    setHydrationError(null);

    apiService
      .getChatMessages(sessionId)
      .then((res: any) => {
        if (cancelled) return;
        const rows: any[] = res?.messages || [];
        setChatMessages((current) => {
          if (current.length > 0) return current;
          return rows.map((m, i) => ({
            id: `${m.id ?? 'hydrated'}-${i}`,
            role: m.role,
            content: m.content,
          }));
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setHydrationError(
          err instanceof Error ? err.message : 'Could not load this conversation.',
        );
      })
      .finally(() => {
        if (!cancelled) setHydratedSession(sessionId);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, hydrationAttempt]);

  const awaitingHydration = !!sessionId && hydratedSession !== sessionId;

  const handleScopedChat = async () => {
    const trimmed = chatInput.trim();
    // Synchronous guard FIRST -- state alone cannot stop a same-frame double
    // submit. Sending is also gated on hydration so a new message cannot
    // interleave with backfilled history.
    if (sendInFlightRef.current) return;
    if (!trimmed || !sessionId || sessionLoading || awaitingHydration) return;
    sendInFlightRef.current = true;

    // Issue #784: per-submit key so the backend IdempotencyMiddleware can
    // dedupe cross-tab / network-retry duplicates.
    const idempotencyKey = mintIdempotencyKey();

    setChatInput('');
    setChatLoading(true);
    setChatMessages((prev) => [
      ...prev,
      { id: `local-${idempotencyKey}`, role: 'user', content: trimmed },
    ]);

    try {
      const clauseInfo = clauseContext
        ? `\nClause: "${clauseContext.clauseTitle}"\n"${clauseContext.clauseText}"`
        : '';

      const contextInjection = `[Redline Finding: "${context.findingTitle}" (${context.severity} severity, ${findingTypeLabel})]${context.changeSummary ? `\n[Changes: ${context.changeSummary}]` : ''}${context.commentSummary ? `\n[Comments: ${context.commentSummary}]` : ''}${context.riskAnalysis ? `\n[Risk: ${context.riskAnalysis}]` : ''}${clauseInfo}`;

      // Plan 143: finding context travels as contextInjection, NOT wrapped
      // into the user's message. Previously the whole finding was inlined
      // into the prompt and persisted into the general matter chat as an
      // unexplained wall of text.
      const data = await apiService.sendChatMessage(sessionId, trimmed, {
        contextInjection,
        idempotencyKey,
      });
      setChatMessages((prev) => [
        ...prev,
        {
          id: `assistant-${idempotencyKey}`,
          role: 'assistant',
          content: data.assistant_message?.content || 'No response generated.',
        },
      ]);
    } catch (err) {
      console.error('[NegotiationDetailView] Scoped chat error:', err);
      setChatMessages((prev) => [
        ...prev,
        {
          id: `error-${idempotencyKey}`,
          role: 'assistant',
          content: `Error: ${err instanceof Error ? err.message : 'Failed to get response.'}`,
        },
      ]);
    } finally {
      sendInFlightRef.current = false;
      setChatLoading(false);
    }
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleScopedChat();
    }
  };

  return (
    <div className="negotiation-detail-view" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="context-view-header">
        <button
          className="right-panel-back-btn"
          onClick={draftResult ? () => setDraftResult(null) : onBack}
          title="Back"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
      </div>

      {/* Body - scrollable */}
      <div className="context-view-body" ref={bodyRef}>
        {/* Badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <span className={`severity-badge ${severityClass}`}>
            {SEVERITY_LABELS[severityClass] || severityClass}
          </span>
          <span className={`negotiation-detail__type-badge negotiation-detail__type-badge--${context.findingType}`}>
            {findingTypeLabel}
          </span>
        </div>
        {/* Title */}
        <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, lineHeight: 1.4 }}>
          {context.findingTitle}
        </h3>

        {/* Plan 196: playbook attribution block — renders only when
            the agent declared a tier + clause_type AND that resolved
            to a real playbook position. */}
        {context.sourcePositionId && (
          <PlaybookAttribution
            positionId={context.sourcePositionId}
            matchedTier={context.matchedPlaybookTier ?? null}
          />
        )}

        {/* Response strength + comment audience: collapsed when draft exists */}
        {initialDraftLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <div className="clause-actions-loading">
              <div className="clause-actions-loading-dot" />
              <div className="clause-actions-loading-dot" />
              <div className="clause-actions-loading-dot" />
            </div>
          </div>
        ) : draftResult ? (
          <div style={{ marginBottom: '16px', padding: '8px 12px', background: 'var(--bg-elevated, #f9fafb)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '13px' }}>
              <span style={{ fontWeight: 600 }}>{RESPONSE_STRENGTHS.find(s => s.value === responseStrength)?.label || responseStrength}</span>
              <span style={{ color: 'var(--text-tertiary)', marginLeft: '8px' }}>
                {commentAudience === 'internal' ? 'Internal' : 'External'} comment
              </span>
            </div>
            <button
              className="clause-card__view-toggle-btn"
              onClick={() => setDraftResult(null)}
              style={{ fontSize: '11px', color: 'var(--accent-blue, #3b82f6)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            {/* Response strength selector */}
            <div style={{ marginBottom: '16px' }}>
              <div className="issue-detail-section-label">Response Strength</div>
              <div className="negotiation-detail__strength-selector">
                {RESPONSE_STRENGTHS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`negotiation-detail__strength-option ${responseStrength === opt.value ? 'negotiation-detail__strength-option--selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="response-strength"
                      value={opt.value}
                      checked={responseStrength === opt.value}
                      onChange={() => {
                        strengthTouchedRef.current = true;
                        setResponseStrength(opt.value);
                      }}
                      className="negotiation-detail__strength-radio"
                    />
                    <div>
                      <div className="negotiation-detail__strength-label">{opt.label}</div>
                      <div className="negotiation-detail__strength-desc">{opt.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Comment audience toggle */}
            <div style={{ marginBottom: '16px' }}>
              <div className="issue-detail-section-label">Comment Audience</div>
              <div className="negotiation-detail__audience-toggle">
                <button
                  className={`negotiation-detail__audience-btn ${commentAudience === 'external' ? 'negotiation-detail__audience-btn--active' : ''}`}
                  onClick={() => setCommentAudience('external')}
                >
                  External (to counterparty)
                </button>
                <button
                  className={`negotiation-detail__audience-btn ${commentAudience === 'internal' ? 'negotiation-detail__audience-btn--active' : ''}`}
                  onClick={() => setCommentAudience('internal')}
                >
                  Internal (to client)
                </button>
              </div>
            </div>
          </>
        )}

        {/* Plan 235 / #967: the full ordered thread, persisted per finding. */}
        {hydrationError && (
          <div className="clause-actions-chat-response">
            <div className="clause-actions-explanation-content">
              <span>{hydrationError}</span>
              <button
                type="button"
                className="context-action-btn"
                onClick={() => setHydrationAttempt((n) => n + 1)}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {chatMessages.map((m) => (
          <div key={m.id} className="clause-actions-chat-response">
            <div className="clause-actions-explanation-header">
              {m.role === 'user' ? 'You' : 'Response'}
            </div>
            <div className="clause-actions-explanation-content desk-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
            </div>
          </div>
        ))}

        {chatLoading && (
          <div className="clause-actions-chat-response">
            <div className="clause-actions-explanation-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Thinking...
            </div>
            <div className="clause-actions-loading">
              <div className="clause-actions-loading-dot" />
              <div className="clause-actions-loading-dot" />
              <div className="clause-actions-loading-dot" />
            </div>
          </div>
        )}

        {/* Draft loading */}
        {draftLoading && (
          <div className="clause-actions-chat-response" style={{ borderLeft: '3px solid var(--accent-blue, #3b82f6)' }}>
            <div className="clause-actions-explanation-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Drafting response...
            </div>
            <div className="clause-actions-loading">
              <div className="clause-actions-loading-dot" />
              <div className="clause-actions-loading-dot" />
              <div className="clause-actions-loading-dot" />
            </div>
          </div>
        )}

        {/* Draft error */}
        {draftError && (
          <div className="clause-actions-chat-response" style={{ borderLeft: '3px solid var(--severity-high, #ef4444)' }}>
            <div className="clause-actions-explanation-header">Error</div>
            <div className="clause-actions-explanation-content" style={{ color: 'var(--severity-high, #ef4444)' }}>
              {draftError}
            </div>
          </div>
        )}

        {/* Draft result */}
        {!initialDraftLoading && draftResult && (
          <div className="negotiation-detail__draft-result" style={{ marginBottom: '16px' }}>
            {isAccepted && (
              <div style={{
                marginBottom: '8px', padding: '6px 12px',
                background: 'var(--green-50, #f0fdf4)', border: '1px solid var(--green-200, #bbf7d0)',
                borderRadius: '6px', fontSize: '12px', color: 'var(--green-800, #166534)',
                fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Accepted
              </div>
            )}
            <div className="issue-detail-section-label">Drafted Response</div>
            {draftResult.proximity_warning && (
              <div style={{
                marginBottom: '8px', padding: '8px 12px',
                background: 'var(--amber-50, #fffbeb)', border: '1px solid var(--amber-200, #fde68a)',
                borderRadius: '6px', fontSize: '12px', color: 'var(--amber-800, #92400e)',
              }}>
                This draft may retain more counterparty changes than intended for this
                strength level. Consider refining or selecting a different strength.
              </div>
            )}
            {draftResult.has_revision && (
              <div style={{ marginBottom: '8px', padding: '8px 12px', background: 'var(--bg-elevated, #f9fafb)', borderRadius: '6px', fontSize: '13px' }}>
                <span style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'block', marginBottom: '4px' }}>Counterparty text &rarr; Your proposal</span>
                {draftResult.baseline_text && draftResult.proposed_text ? (
                  <RevisionDiff original={draftResult.baseline_text} revised={draftResult.proposed_text} />
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Revision saved</span>
                )}
              </div>
            )}
            {draftResult.has_comment && (
              <div style={{ marginBottom: '8px', padding: '8px 12px', background: 'var(--bg-elevated, #f9fafb)', borderRadius: '6px', fontSize: '13px' }}>
                <span style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'block', marginBottom: '4px' }}>
                  Comment {draftResult.comment_audience === 'internal' ? '(internal)' : '(to counterparty)'}
                </span>
                {draftResult.comment_text && (
                  <blockquote style={{ margin: '4px 0 0', padding: '4px 8px', borderLeft: '2px solid var(--ws-border-subtle, #d0d0d0)', fontStyle: 'italic', color: 'var(--ws-text-secondary)' }}>
                    {draftResult.comment_text}
                  </blockquote>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {!isAccepted && (
                <button className="context-action-btn primary" onClick={handleAcceptDraft} style={{ flex: 1 }}>
                  Accept
                </button>
              )}
              <button className="context-action-btn" onClick={handleRefineDraft} style={{ flex: 1 }}>
                Refine
              </button>
              <button className="context-action-btn" onClick={async () => {
                setDraftResult(null);
                setShowRefineInput(false);
                // Reject draft: revert clause state to reviewed
                if (context.clauseId) {
                  try {
                    await updateClauseStateMutation.mutateAsync({
                      analysisRunId: context.analysisRunId,
                      clauseId: context.clauseId,
                      data: { status: 'reviewed' },
                    });
                  } catch (err) {
                    console.error('[NegotiationDetailView] Failed to reject draft:', err);
                  }
                }
              }} style={{ flex: 1 }}>
                Reject
              </button>
            </div>
            {showRefineInput && (
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                <textarea
                  className="clause-actions-scoped-chat-input"
                  value={refinePrompt}
                  onChange={(e) => setRefinePrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitRefinement(); } }}
                  placeholder="How should the draft be refined?"
                  rows={2}
                  autoFocus
                  style={{ flex: 1 }}
                />
                {/* Plan 235 Move 5: a sub-action inside the refine affordance,
                    not the row's commit -- Accept above it is the primary. */}
                <button
                  className="context-action-btn"
                  onClick={handleSubmitRefinement}
                  disabled={!refinePrompt.trim()}
                  style={{ alignSelf: 'flex-end', padding: '6px 12px' }}
                >
                  Send
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action buttons — single non-wrapping row so Draft / Skip / Dismiss
          (and Apply-in-Word) always share one line regardless of panel width.
          The primary button flexes and its label ellipsizes rather than
          forcing the secondary buttons to wrap. */}
      <div className="context-view-actions">
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px', width: '100%' }}>
          {clauseContext && !draftResult && (
            <>
              <button
                className="context-action-btn primary"
                onClick={() => handleDraftResponse()}
                disabled={!responseStrength || draftLoading}
                title={!responseStrength ? 'Select a response strength first' : 'Draft a response for this clause'}
                style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {draftLoading
                    ? (responseStrength === 'completely_accept' ? 'Accepting...'
                      : responseStrength === 'completely_reject' ? 'Rejecting...'
                      : 'Drafting...')
                    : (responseStrength === 'completely_accept' ? 'Accept'
                      : responseStrength === 'completely_reject' ? 'Reject'
                      : 'Draft reply')}
                </span>
              </button>
              <button
                className="context-action-btn"
                onClick={async () => {
                  if (context.clauseId) {
                    try {
                      await updateClauseStateMutation.mutateAsync({
                        analysisRunId: context.analysisRunId,
                        clauseId: context.clauseId,
                        data: { status: 'skipped' },
                      });
                    } catch (err) {
                      console.error('[NegotiationDetailView] Failed to skip clause:', err);
                    }
                  }
                  onBack();
                }}
                title="Skip this clause (no decision)"
                style={{ flexShrink: 0 }}
              >
                Skip
              </button>
            </>
          )}
          {/* Plan 180: Dismiss this finding (soft delete, hidden from default view). */}
          <button
            type="button"
            className="context-action-btn"
            onClick={handleDismiss}
            disabled={dismissing || !context.analysisRunId}
            title="Dismiss this finding"
            style={{ flexShrink: 0 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            {dismissing ? 'Dismissing...' : 'Dismiss'}
          </button>
        </div>
      </div>

      {/* Scoped chat input */}
      <div className="clause-actions-scoped-chat">
        <textarea
          className="clause-actions-scoped-chat-input"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={handleChatKeyDown}
          placeholder="Ask about this finding..."
          rows={1}
          disabled={chatLoading || !sessionId || sessionLoading || awaitingHydration}
        />
        <button
          className="clause-actions-scoped-chat-send"
          onClick={handleScopedChat}
          disabled={chatLoading || !chatInput.trim() || !sessionId || sessionLoading || awaitingHydration}
          title="Send"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NegotiationDetailView;
